import { on } from 'delegated-events';
import { createFocusTrap, type FocusTrap } from 'focus-trap';
import collapse from '../../../javascripts/utils/collapse';
import moveFocus from '../../../javascripts/utils/moveFocus';
import onLostFocus from '../../../javascripts/utils/onLostFocus';
import onLoad from '../../../javascripts/utils/onLoad';
import abort from '../../../javascripts/utils/abort';

let $currentOpenLink: HTMLElement | null = null;
let currentLostFocusHandler: CallableFunction | null = null;
let focusTrap: FocusTrap | null = null;
let returnHighlight: CallableFunction | null = null;

on('click', '.header__navigation-item [aria-controls]', async (event) => {
  const { currentTarget: $trigger } = event;

  event.preventDefault();

  const { isOpen, $target } = await collapse($trigger);

  if (isOpen) {
    if (currentLostFocusHandler) {
      currentLostFocusHandler();
    }

    moveFocus($target);

    currentLostFocusHandler = onLostFocus($target, () => {
      collapse($trigger, false);
      returnHighlight?.();
      $currentOpenLink = null;
    });

    $currentOpenLink = $trigger.closest('.header__navigation-item')
      ?.querySelector<HTMLElement>('.header__navigation-link') ?? null;
  } else if (currentLostFocusHandler) {
    currentLostFocusHandler();
    returnHighlight?.();

    currentLostFocusHandler = null;
    $currentOpenLink = null;
  }
});

on('click', '.header [data-action="open"]', async (event) => {
  const { currentTarget: $trigger } = event;

  event.preventDefault();

  const { $target } = await collapse($trigger, true);

  focusTrap = createFocusTrap($target, {
    initialFocus: '.header__navigation-item a, .header__navigation-item button',
    clickOutsideDeactivates: false,
    returnFocusOnDeactivate: true,
    async onDeactivate() {
      await collapse($trigger, false);
    },
    onPostDeactivate() {
      focusTrap = null;
    },
  });

  focusTrap.activate();
});

on('click', '.header [data-action="close"]', async (event) => {
  event.preventDefault();
  focusTrap?.deactivate();
});

onLoad<HTMLElement>('.header', ($header) => {
  const resizeObserver = new ResizeObserver(() => {
    focusTrap?.deactivate();
  });

  resizeObserver.observe($header);

  const $highlight = $header.querySelector<HTMLElement>('.header__navigation-highlight') ?? abort();
  const $menu = $header.querySelector('.header__menu') ?? abort();
  const $currentActiveLink = $header.querySelector<HTMLElement>('.header__navigation-link [aria-current=true]');

  let $currentHighlight: HTMLElement | null = $currentActiveLink;

  const moveHighlight = ($target: HTMLElement | null) => {
    if (!$target) {
      $highlight.style.opacity = '0';
    } else {
      const { offsetWidth: inlineSize } = $target;
      const { left: targetInlineStart } = $target.getBoundingClientRect();
      const { left: menuInlineStart } = $menu.getBoundingClientRect();

      $highlight.style.opacity = '1';
      $highlight.style.insetInlineStart = `${targetInlineStart - menuInlineStart - 5}px`;
      $highlight.style.inlineSize = `${inlineSize + 10}px`;
    }
  };

  const highlightResizeObserver = new ResizeObserver(() => {
    moveHighlight($currentHighlight);
  });

  moveHighlight($currentHighlight);

  highlightResizeObserver.observe($menu);

  returnHighlight = () => moveHighlight($currentActiveLink);

  $header.querySelectorAll<HTMLElement>('.header__navigation-link').forEach(($link) => {
    $link.addEventListener('mouseenter', () => {
      $currentHighlight = $link;
      moveHighlight($link);
    });

    $link.addEventListener('mouseleave', () => {
      $currentHighlight = $currentActiveLink;
      moveHighlight($currentOpenLink ?? $currentActiveLink);
    });
  });
});
